import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { Wallet, Users, ArrowUpRight, ArrowDownLeft, X, Plus, Search, ChevronDown, ChevronUp, CreditCard, FileText, Landmark, AlertTriangle, CheckCircle2, Fuel, Wrench, GitMerge, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const badge = (v) => {
  if (v > 0) return { cls: "text-[#DC2626] bg-[#FEE2E2]", label: "Borçlu" };
  if (v < 0) return { cls: "text-[#16A34A] bg-[#DCFCE7]", label: "Fazla Ödeme" };
  return { cls: "text-[#0A0A0A] bg-[#F5F5F5]", label: "Denk" };
};

export default function SupplierAccountsPage() {
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showEntry, setShowEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({
    type: "borc", date: new Date().toISOString().slice(0,10),
    amount: "", description: "", invoice_no: "", category: "",
  });
  const [payForm, setPayForm] = useState({ amount: "", date: new Date().toISOString().slice(0,10), description: "" });
  const [onlyDebt, setOnlyDebt] = useState(false);

  // Reconciliation state
  const [showRecon, setShowRecon] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [selectedForFix, setSelectedForFix] = useState(new Set());
  const [reconApplying, setReconApplying] = useState(false);

  // Fuel deduction state
  const [showFuel, setShowFuel] = useState(false);
  const [fuelForm, setFuelForm] = useState({
    month: new Date().toISOString().slice(0,7),
    amount: "", description: "",
  });

  // Dedupe suppliers state
  const [showDedupe, setShowDedupe] = useState(false);
  const [dedupe, setDedupe] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [keeperMap, setKeeperMap] = useState({});
  const [dedupeApplying, setDedupeApplying] = useState(false);

  // Edit supplier state
  const [editingSup, setEditingSup] = useState(null);
  const [addingSup, setAddingSup] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "", category: "", tax_office: "", tax_no: "", iban: "", phone: "", contact: "", address: "", plate: "",
  });

  // Edit event (payment or invoice inside statement) state
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ amount: "", date: "", description: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/suppliers/statements/summary");
      setSummary(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const openStatement = async (sup) => {
    setSelected(sup);
    setStatementLoading(true);
    setStatement(null);
    try {
      const { data } = await api.get(`/suppliers/${sup.id}/statement`);
      setStatement(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setStatementLoading(false); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/supplier-payments/quick", {
        supplier_id: selected.id,
        amount: Number(payForm.amount) || 0,
        date: payForm.date,
        description: payForm.description,
      });
      toast.success("Ödeme kaydedildi");
      setShowPay(false);
      setPayForm({ amount: "", date: new Date().toISOString().slice(0,10), description: "" });
      load();
      openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitEntry = async (e) => {
    e.preventDefault();
    const amt = Number(entryForm.amount) || 0;
    if (amt <= 0) { toast.error("Tutar pozitif olmalı"); return; }
    try {
      if (entryForm.type === "borc") {
        // Create supplier invoice
        await api.post("/invoices", {
          invoice_no: entryForm.invoice_no || `MAN-${Date.now()}`,
          supplier_id: selected.id,
          supplier_or_type: selected.name,
          date: entryForm.date,
          due_date: entryForm.date,
          amount: amt,
          status: "unpaid",
          category: entryForm.category || "Cari Hareket",
          month: entryForm.date.slice(0,7),
          notes: entryForm.description,
        });
        toast.success("Borç kaydı (fatura) eklendi");
      } else {
        // Alacak = payment
        await api.post("/supplier-payments/quick", {
          supplier_id: selected.id,
          amount: amt,
          date: entryForm.date,
          description: entryForm.description || "Manuel alacak kaydı",
        });
        toast.success("Alacak kaydı (ödeme) eklendi");
      }
      setShowEntry(false);
      setEntryForm({ type: "borc", date: new Date().toISOString().slice(0,10), amount: "", description: "", invoice_no: "", category: "" });
      load();
      openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openReconciliation = async () => {
    setShowRecon(true);
    setReconLoading(true);
    setRecon(null);
    setSelectedForFix(new Set());
    try {
      const { data } = await api.get("/supplier-reconciliation");
      setRecon(data);
      // Preselect all mismatches
      setSelectedForFix(new Set(data.mismatches.map(m => m.id)));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReconLoading(false); }
  };

  const toggleReconRow = (id) => {
    const next = new Set(selectedForFix);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedForFix(next);
  };

  const applyReconciliation = async () => {
    if (selectedForFix.size === 0) { toast.error("En az bir tedarikçi seçin"); return; }
    if (!window.confirm(`${selectedForFix.size} tedarikçi için otomatik ödeme kaydı oluşturulacak. Onaylıyor musunuz?`)) return;
    setReconApplying(true);
    try {
      const { data } = await api.post("/supplier-reconciliation/apply", {
        supplier_ids: Array.from(selectedForFix),
      });
      toast.success(`${data.created_count} tedarikçi düzeltildi · Toplam: ${formatTL(data.total_amount)}`);
      setShowRecon(false);
      await load();
      if (selected) await openStatement(selected);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReconApplying(false); }
  };

  const submitFuelDeduction = async (e) => {
    e.preventDefault();
    const amt = Number(fuelForm.amount) || 0;
    if (amt <= 0) { toast.error("Tutar pozitif olmalı"); return; }
    if (!/^\d{4}-\d{2}$/.test(fuelForm.month)) { toast.error("Geçerli ay giriniz (YYYY-MM)"); return; }
    try {
      await api.post(`/suppliers/${selected.id}/fuel-deduction`, {
        month: fuelForm.month,
        amount: amt,
        description: fuelForm.description,
      });
      toast.success(`${fuelForm.month} ayı için yakıt kesintisi eklendi: ${formatTL(amt)}`);
      setShowFuel(false);
      setFuelForm({ month: new Date().toISOString().slice(0,7), amount: "", description: "" });
      load();
      openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openDedupe = async () => {
    setShowDedupe(true);
    setDedupeLoading(true);
    setDedupe(null);
    try {
      const { data } = await api.get("/admin/dedupe-suppliers/preview");
      setDedupe(data);
      // Initialize keeperMap with suggested keepers
      const map = {};
      for (const g of data.groups) {
        map[g.key] = g.suggested_keeper_id;
      }
      setKeeperMap(map);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDedupeLoading(false); }
  };

  const applyDedupe = async () => {
    if (!dedupe || dedupe.group_count === 0) return;
    if (!window.confirm(`${dedupe.group_count} gruptan ${dedupe.duplicates_to_remove} mükerrer tedarikçi kaldırılacak. Faturalar ve ödemeler tutulan kayda taşınacak. Onaylıyor musunuz?`)) return;
    setDedupeApplying(true);
    try {
      const { data } = await api.post("/admin/dedupe-suppliers", { keeper_map: keeperMap });
      toast.success(`${data.duplicates_removed} mükerrer birleştirildi · ${data.invoices_reassigned} fatura + ${data.payments_reassigned} ödeme taşındı`);
      setShowDedupe(false);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDedupeApplying(false); }
  };

  const openEditSupplier = (s) => {
    setEditingSup(s);
    setEditForm({
      name: s.name || "",
      category: s.category || "",
      tax_office: s.tax_office || "",
      tax_no: s.tax_no || "",
      iban: s.iban || "",
      phone: s.phone || "",
      contact: s.contact || "",
      address: s.address || "",
      plate: s.plate || "",
    });
  };

  const openAddSupplier = () => {
    setAddingSup(true);
    setEditForm({ name: "", category: "", tax_office: "", tax_no: "", iban: "", phone: "", contact: "", address: "", plate: "" });
  };

  const submitAddSupplier = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("İsim zorunlu"); return; }
    try {
      await api.post("/suppliers", editForm);
      toast.success("Yeni tedarikçi eklendi");
      setAddingSup(false);
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitEditSupplier = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("İsim zorunlu"); return; }
    try {
      await api.put(`/suppliers/${editingSup.id}`, editForm);
      toast.success("Tedarikçi güncellendi");
      setEditingSup(null);
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteSupplier = async (s) => {
    const hasActivity = (s.invoice_count || 0) + (s.payment_count || 0) + (s.bank_matched_count || 0) > 0;
    const msg = hasActivity
      ? `"${s.name}" tedarikçisinin ${s.invoice_count || 0} fatura ve ${s.payment_count || 0} ödeme kaydı var. Bunlar SİLİNMEZ ama tedarikçi kaydı silinecek → cari sayfasında görünmez olur. Devam edilsin mi?`
      : `"${s.name}" tedarikçisi silinecek. Onaylıyor musunuz?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Tedarikçi silindi");
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteEvent = async (event) => {
    let msg = "";
    if (event.type === "payment") msg = `${formatTL(event.credit)} tutarlı ödeme kaydı silinecek. Onaylıyor musunuz?`;
    else if (event.type === "invoice") msg = `${event.reference || "Fatura"} (${formatTL(event.debit)}) silinecek. Onaylıyor musunuz?`;
    else if (event.type === "bank") msg = `Bu banka hareketinin fatura eşleşmesi kaldırılacak (banka kaydı silinmez). Devam?`;
    if (!window.confirm(msg)) return;
    try {
      if (event.type === "payment") {
        await api.delete(`/supplier-payments/${event.id}`);
        toast.success("Ödeme silindi");
      } else if (event.type === "invoice") {
        await api.delete(`/invoices/${event.id}`);
        toast.success("Fatura silindi");
      } else if (event.type === "bank") {
        await api.post(`/bank-transactions/${event.id}/unmatch`);
        toast.success("Banka eşleşmesi kaldırıldı");
      }
      openStatement(selected);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      amount: (event.type === "invoice" ? event.debit : event.credit) || "",
      date: (event.date || "").slice(0, 10),
      description: event.description || "",
    });
  };

  const submitEditEvent = async (e) => {
    e.preventDefault();
    const amt = Number(eventForm.amount) || 0;
    if (amt <= 0) { toast.error("Tutar pozitif olmalı"); return; }
    try {
      if (editingEvent.type === "payment") {
        await api.put(`/supplier-payments/${editingEvent.id}`, {
          supplier_id: selected.id,
          amount: amt,
          date: eventForm.date,
          description: eventForm.description,
        });
        toast.success("Ödeme güncellendi");
      } else if (editingEvent.type === "invoice") {
        // Preserve original invoice fields (need supplier_or_type, status, etc.)
        await api.put(`/invoices/${editingEvent.id}`, {
          invoice_no: editingEvent.reference || "",
          supplier_id: selected.id,
          supplier_or_type: selected.name,
          date: eventForm.date,
          due_date: eventForm.date,
          amount: amt,
          status: editingEvent.status || "unpaid",
          category: "Cari Hareket",
          month: eventForm.date.slice(0, 7),
          notes: eventForm.description,
        });
        toast.success("Fatura güncellendi");
      }
      setEditingEvent(null);
      openStatement(selected);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!summary) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;

  const q = search.toLowerCase().trim();
  const filtered = summary.suppliers.filter(s => {
    if (onlyDebt && s.balance <= 0) return false;
    if (!q) return true;
    return (s.name + " " + s.category + " " + s.tax_no + " " + s.iban + " " + s.plate).toLowerCase().includes(q);
  });

  return (
    <>
      <PageHeader
        title="Tedarikçi Cari Hesaplar"
        subtitle="Her tedarikçi için fatura, ödeme ve bakiye takibi. Ekstre satırları kronolojik akış."
        testid="tedarikci-cari-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="btn-add-supplier"
              onClick={openAddSupplier}
              className="inline-flex items-center gap-2 bg-[#16A34A] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#15803D]"
            >
              <Plus className="w-3.5 h-3.5" /> Yeni Cari Kart
            </button>
            <button
              data-testid="btn-open-reconciliation"
              onClick={openReconciliation}
              className="inline-flex items-center gap-2 bg-[#F59E0B] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#D97706]"
            >
              <Wrench className="w-3.5 h-3.5" /> Mutabakat
            </button>
            <button
              data-testid="btn-open-dedupe"
              onClick={openDedupe}
              className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#6D28D9]"
            >
              <GitMerge className="w-3.5 h-3.5" /> Mükerrer Birleştir
            </button>
            <ExportButtons
            filename="tedarikci_cari"
            title="Tedarikçi Cari Bakiye"
            subtitle={`${summary.suppliers.length} tedarikçi · Toplam borç: ${formatTL(summary.grand.balance)} · Ödenmemiş fatura: ${formatTL(summary.grand.unpaid_total)}`}
            sections={[
              { name: "Cari Bakiye", heading: "Tedarikçi Cari Bakiye",
                columns: [
                  { key: "name", label: "Tedarikçi" },
                  { key: "category", label: "Kategori" },
                  { key: "invoice_count", label: "Fat. Sayısı", format: "num" },
                  { key: "invoice_total", label: "Fatura Toplamı", format: "money" },
                  { key: "payment_total", label: "Manuel Ödeme", format: "money" },
                  { key: "bank_matched_total", label: "Banka Ödemesi", format: "money" },
                  { key: "balance", label: "Kalan Bakiye", format: "money" },
                ],
                rows: summary.suppliers,
              },
            ]}
          />
          </div>
        }
      />

      {/* Grand KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tedarikçi Sayısı</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(summary.suppliers.length)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura Toplamı</div>
          <div className="num font-bold text-xl mt-1">{formatTL(summary.grand.invoice_total)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödemeler</div>
          <div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatTL(summary.grand.payment_total + summary.grand.bank_matched)}</div>
          <div className="text-[10px] text-neutral-400 mt-0.5">Manuel + Banka</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödenmemiş Fatura</div>
          <div className="num font-bold text-xl mt-1 text-[#F59E0B]">{formatTL(summary.grand.unpaid_total)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Borç</div>
          <div className={`num font-bold text-xl mt-1 ${summary.grand.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(summary.grand.balance)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-3 rounded-md mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-neutral-400" />
          <input data-testid="cari-search" placeholder="Tedarikçi, vergi no, IBAN, plaka ara..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 px-2 py-1.5 text-sm border-0 focus:outline-none" />
        </div>
        <button onClick={() => setOnlyDebt(!onlyDebt)} data-testid="filter-only-debt" className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${onlyDebt ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
          Sadece Borçlular ({summary.suppliers.filter(s => s.balance > 0).length})
        </button>
      </div>

      {/* Supplier list */}
      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr>
                {["İşlem","Tedarikçi","Vergi No / IBAN","Fatura","Ödeme","Kalan Bakiye","Durum"].map((h, i) => (
                  <th key={h} className={`px-3 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 3 && i <= 5 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-sm text-neutral-500">Tedarikçi bulunamadı</td></tr>
              ) : filtered.map(s => {
                const b = badge(s.balance);
                return (
                  <tr key={s.id} className="border-b border-[#F5F5F5]" data-testid={`cari-row-${s.id}`}>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`cari-open-${s.id}`} onClick={() => openStatement(s)} className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold hover:bg-[#0020C2]" title="Ekstre görüntüle">
                          <FileText className="w-3 h-3" /> Ekstre
                        </button>
                        <button data-testid={`tedarikci-edit-${s.id}`} onClick={() => openEditSupplier(s)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#0020C2] hover:text-white hover:border-[#0020C2] transition-colors" title="Tedarikçi bilgilerini düzenle">
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button data-testid={`tedarikci-delete-${s.id}`} onClick={() => deleteSupplier(s)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626] transition-colors" title="Tedarikçiyi sil">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-semibold">{s.name}</div>
                      <div className="text-xs text-neutral-500">{s.category || "-"} {s.plate && `· ${s.plate}`}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-neutral-600">
                      {s.tax_no && <div>{s.tax_office && `${s.tax_office} · `}VKN {s.tax_no}</div>}
                      {s.iban && <div className="font-mono text-[10px] mt-0.5">{s.iban}</div>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="num font-semibold">{formatTL(s.invoice_total)}</div>
                      <div className="text-[10px] text-neutral-500">{s.invoice_count} adet</div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="num font-semibold text-[#16A34A]">{formatTL(s.total_paid)}</div>
                      <div className="text-[10px] text-neutral-500">
                        {s.payment_count > 0 && `${s.payment_count} manuel `}
                        {s.bank_matched_count > 0 && `${s.bank_matched_count} banka`}
                        {(s.payment_count + s.bank_matched_count) === 0 && "-"}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className={`num font-bold text-base ${s.balance > 0 ? "text-[#DC2626]" : s.balance < 0 ? "text-[#16A34A]" : "text-[#0A0A0A]"}`}>{formatTL(Math.abs(s.balance))}</div>
                      {s.balance > 0 && <div className="text-[10px] text-[#DC2626]">Ödenecek</div>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${b.cls}`}>{b.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statement drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end p-0" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-4xl shadow-lg overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cari Ekstre</div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
                {(selected.tax_office || selected.tax_no) && (
                  <div className="text-xs text-neutral-500 mt-0.5">{selected.tax_office} · VKN {selected.tax_no}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {statement && (
                  <ExportButtons
                    filename={`ekstre_${selected.name.replace(/[^a-zA-Z0-9]/g, "_")}`}
                    title={`Cari Ekstre — ${selected.name}`}
                    subtitle={`${selected.tax_office ? selected.tax_office + " · " : ""}${selected.tax_no ? "VKN " + selected.tax_no + " · " : ""}Borç: ${formatTL(statement.summary.invoice_total)} · Alacak: ${formatTL(statement.summary.payment_total)} · Bakiye: ${formatTL(statement.summary.balance)}`}
                    sections={[
                      { name: "Ekstre", heading: "Cari Hareket Ekstresi",
                        columns: [
                          { key: "date", label: "Tarih" },
                          { key: "label", label: "Tip / Açıklama" },
                          { key: "reference", label: "Ref" },
                          { key: "debit", label: "Borç", format: "money" },
                          { key: "credit", label: "Alacak", format: "money" },
                          { key: "running_balance", label: "Bakiye", format: "money" },
                        ],
                        rows: statement.events,
                      },
                    ]}
                  />
                )}
                <button data-testid="cari-entry-btn" onClick={() => setShowEntry(true)} className="flex items-center gap-2 bg-[#0020C2] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0018A0]">
                  <Plus className="w-3.5 h-3.5" /> Cari Hareket
                </button>
                <button data-testid="cari-fuel-btn" onClick={() => setShowFuel(true)} className="flex items-center gap-2 bg-[#F59E0B] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#D97706]">
                  <Fuel className="w-3.5 h-3.5" /> Yakıt Kes
                </button>
                <button data-testid="cari-pay-btn" onClick={() => setShowPay(true)} className="flex items-center gap-2 bg-[#16A34A] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#15803D]">
                  <CreditCard className="w-3.5 h-3.5" /> Hızlı Ödeme
                </button>
                <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {statementLoading ? (
                <div className="text-center py-12 text-sm text-neutral-500">Ekstre yükleniyor...</div>
              ) : !statement ? null : (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Fatura (Borç)</div>
                      <div className="num font-bold text-lg text-[#DC2626] mt-1">{formatTL(statement.summary.invoice_total)}</div>
                      <div className="text-[10px] text-neutral-500">{statement.summary.invoice_count} fatura</div>
                    </div>
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ödeme (Alacak)</div>
                      <div className="num font-bold text-lg text-[#16A34A] mt-1">{formatTL(statement.summary.payment_total)}</div>
                      <div className="text-[10px] text-neutral-500">{statement.summary.payment_count} ödeme</div>
                    </div>
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kalan Bakiye</div>
                      <div className={`num font-bold text-lg mt-1 ${statement.summary.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(statement.summary.balance)}</div>
                      <div className="text-[10px] text-neutral-500">{statement.summary.balance > 0 ? "Ödenecek" : "Denk / Fazla"}</div>
                    </div>
                  </div>

                  {/* Unmatched paid invoices warning */}
                  {statement.unmatched_paid_invoices && statement.unmatched_paid_invoices.length > 0 && (
                    <div className="mt-3 border-2 border-[#DC2626] bg-[#FEE2E2] p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" />
                          <div>
                            <div className="font-heading font-bold text-[#DC2626] text-sm">
                              {statement.unmatched_paid_invoices.length} adet &quot;ÖDENDİ&quot; işaretli fatura, cari kayıtlarıyla eşleşmiyor
                            </div>
                            <div className="text-[11px] text-[#991B1B] mt-0.5">
                              Bu faturalar ödendi olarak görünüyor ama ödeme kaydı yok. Bakiye hatalı hesaplanıyor. Her fatura için tek tık ile ödeme kaydı oluşturabilirsiniz.
                            </div>
                          </div>
                        </div>
                        <button
                          data-testid="fix-all-unmatched"
                          onClick={async () => {
                            if (!window.confirm(`${statement.unmatched_paid_invoices.length} fatura için otomatik ödeme kaydı oluşturulacak. Onay?`)) return;
                            try {
                              let ok = 0;
                              for (const u of statement.unmatched_paid_invoices) {
                                await api.post(`/invoices/${u.id}/reconcile-payment`, {});
                                ok++;
                              }
                              toast.success(`${ok} fatura senkronlandı`);
                              load();
                              openStatement(selected);
                            } catch (e) { toast.error(errMsg(e)); }
                          }}
                          className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold bg-[#DC2626] text-white hover:bg-[#B91C1C] whitespace-nowrap"
                        >Tümünü Senkronla</button>
                      </div>
                      <div className="border border-[#F87171] bg-white">
                        <table className="w-full text-xs">
                          <thead className="bg-[#FEE2E2] border-b border-[#F87171]">
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-[#991B1B] font-semibold">Fatura No</th>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-[#991B1B] font-semibold">Ay</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#991B1B] font-semibold">Tutar</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-[#991B1B] font-semibold"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {statement.unmatched_paid_invoices.map(u => (
                              <tr key={u.id} className="border-b border-[#FCA5A5]" data-testid={`unmatched-inv-${u.id}`}>
                                <td className="px-3 py-2 font-semibold text-[#991B1B]">{u.invoice_no}</td>
                                <td className="px-3 py-2 num text-neutral-600">{u.month}</td>
                                <td className="px-3 py-2 text-right num font-bold text-[#DC2626]">{formatTL(u.amount)}</td>
                                <td className="px-3 py-2 text-right">
                                  <button
                                    data-testid={`fix-unmatched-${u.id}`}
                                    onClick={async () => {
                                      try {
                                        await api.post(`/invoices/${u.id}/reconcile-payment`, {});
                                        toast.success(`Fatura #${u.invoice_no} senkronlandı`);
                                        load();
                                        openStatement(selected);
                                      } catch (e) { toast.error(errMsg(e)); }
                                    }}
                                    className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold bg-[#DC2626] text-white hover:bg-[#B91C1C]"
                                  >Senkronla</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Timeline table */}
                  {statement.events.length === 0 ? (
                    <div className="text-center py-10 text-sm text-neutral-500 border border-dashed border-[#E5E5E5]">
                      Bu tedarikçi için henüz fatura veya ödeme kaydı yok.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                          <tr>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">İşlem</th>
                            {["Tarih","Tip","Açıklama","Ref","Borç","Alacak","Bakiye"].map((h, i) => (
                              <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 4 ? "text-right" : "text-left"}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {statement.events.map(e => (
                            <tr key={e.type + e.id} className="border-b border-[#F5F5F5]">
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="inline-flex items-center gap-0.5">
                                  {(e.type === "payment" || e.type === "invoice") && (
                                    <button data-testid={`event-edit-${e.id}`} onClick={() => openEditEvent(e)} className="p-1 border border-[#E5E5E5] hover:bg-[#0020C2] hover:text-white hover:border-[#0020C2]" title="Düzenle">
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  )}
                                  <button data-testid={`event-delete-${e.id}`} onClick={() => deleteEvent(e)} className="p-1 border border-[#E5E5E5] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626]" title={e.type === "bank" ? "Eşleşmeyi kaldır" : "Sil"}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                              <td className="px-3 py-2 num whitespace-nowrap">{formatDate(e.date)}</td>
                              <td className="px-3 py-2">
                                {e.type === "invoice" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#DC2626]"><ArrowUpRight className="w-3 h-3" /> {e.label}</span>}
                                {e.type === "payment" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#16A34A]"><ArrowDownLeft className="w-3 h-3" /> {e.label}</span>}
                                {e.type === "bank" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0020C2]"><Landmark className="w-3 h-3" /> {e.label}</span>}
                                {e.status === "paid" && <span className="ml-1 px-1.5 py-0.5 bg-[#DCFCE7] text-[#16A34A] text-[9px] font-semibold uppercase">Ödendi</span>}
                              </td>
                              <td className="px-3 py-2 text-neutral-600 max-w-[280px] truncate" title={e.description}>{e.description || "-"}</td>
                              <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">{e.reference || "-"}</td>
                              <td className="px-3 py-2 text-right num text-[#DC2626] font-semibold">{e.debit > 0 ? formatTL(e.debit) : "-"}</td>
                              <td className="px-3 py-2 text-right num text-[#16A34A] font-semibold">{e.credit > 0 ? formatTL(e.credit) : "-"}</td>
                              <td className={`px-3 py-2 text-right num font-bold ${e.running_balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(e.running_balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-[#FAFAFA] border-t-2 border-[#E5E5E5]">
                          <tr>
                            <td colSpan={5} className="px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">TOPLAM</td>
                            <td className="px-3 py-2 text-right num text-[#DC2626] font-bold">{formatTL(statement.summary.invoice_total)}</td>
                            <td className="px-3 py-2 text-right num text-[#16A34A] font-bold">{formatTL(statement.summary.payment_total)}</td>
                            <td className={`px-3 py-2 text-right num font-bold ${statement.summary.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(statement.summary.balance)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick payment modal */}
      {showPay && selected && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowPay(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hızlı Ödeme</div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
              </div>
              <button onClick={() => setShowPay(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitPayment} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input data-testid="pay-amount" type="number" step="any" required value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                <input type="date" required value={payForm.date} onChange={e => setPayForm({...payForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama</label>
                <input value={payForm.description} onChange={e => setPayForm({...payForm, description: e.target.value})} placeholder="Örn: Ocak faturaları ödemesi" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowPay(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="pay-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Cari Hareket (Borç/Alacak) modal */}
      {showEntry && selected && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowEntry(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cari Hareket Ekle</div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
              </div>
              <button onClick={() => setShowEntry(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitEntry} className="p-6 space-y-4">
              {/* Type toggle */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Hareket Tipi *</label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button type="button" data-testid="entry-type-borc" onClick={() => setEntryForm({...entryForm, type: "borc"})} className={`flex items-center justify-center gap-2 px-3 py-3 text-sm uppercase tracking-widest font-semibold border-2 transition-colors ${entryForm.type === "borc" ? "border-[#DC2626] bg-[#FEE2E2] text-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
                    <ArrowUpRight className="w-4 h-4" /> Borç (Fatura)
                  </button>
                  <button type="button" data-testid="entry-type-alacak" onClick={() => setEntryForm({...entryForm, type: "alacak"})} className={`flex items-center justify-center gap-2 px-3 py-3 text-sm uppercase tracking-widest font-semibold border-2 transition-colors ${entryForm.type === "alacak" ? "border-[#16A34A] bg-[#DCFCE7] text-[#16A34A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
                    <ArrowDownLeft className="w-4 h-4" /> Alacak (Ödeme)
                  </button>
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">
                  {entryForm.type === "borc" ? "Yeni fatura oluşturulur — cari bakiyeye eklenir." : "Manuel ödeme kaydı — bakiyeyi azaltır."}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                  <input data-testid="entry-amount" type="number" step="any" required value={entryForm.amount} onChange={e => setEntryForm({...entryForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                  <input type="date" required value={entryForm.date} onChange={e => setEntryForm({...entryForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              {entryForm.type === "borc" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Fatura No</label>
                    <input value={entryForm.invoice_no} onChange={e => setEntryForm({...entryForm, invoice_no: e.target.value})} placeholder="Otomatik" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kategori</label>
                    <input value={entryForm.category} onChange={e => setEntryForm({...entryForm, category: e.target.value})} placeholder="Cari Hareket" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama</label>
                <textarea rows={2} value={entryForm.description} onChange={e => setEntryForm({...entryForm, description: e.target.value})} placeholder={entryForm.type === "borc" ? "Örn: Devir bakiyesi, ek hizmet bedeli..." : "Örn: Ocak ödemesi..."} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowEntry(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="entry-submit" type="submit" className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold text-white ${entryForm.type === "borc" ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#16A34A] hover:bg-[#15803D]"}`}>
                  {entryForm.type === "borc" ? "Borç Ekle" : "Alacak Ekle"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fuel Deduction modal */}
      {showFuel && selected && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowFuel(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Aylık Yakıt Kesintisi</div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
              </div>
              <button onClick={() => setShowFuel(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitFuelDeduction} className="p-6 space-y-4">
              <div className="bg-[#FFFBEB] border border-[#F59E0B] p-3 text-xs text-[#92400E]">
                Bu tedarikçiden aylık aldığınız yakıt tutarı, cari bakiyesinden <b>alacak (ödeme)</b> olarak düşülür.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Ay (YYYY-MM) *</label>
                  <input data-testid="fuel-month" type="month" required value={fuelForm.month} onChange={e => setFuelForm({...fuelForm, month: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                  <input data-testid="fuel-amount" type="number" step="any" required value={fuelForm.amount} onChange={e => setFuelForm({...fuelForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama</label>
                <input value={fuelForm.description} onChange={e => setFuelForm({...fuelForm, description: e.target.value})} placeholder={`${fuelForm.month} Aylık Yakıt Kesintisi`} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowFuel(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="fuel-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#F59E0B] text-white hover:bg-[#D97706]">Yakıt Kes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reconciliation modal */}
      {showRecon && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowRecon(false)}>
          <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-md shadow-lg flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tedarikçi Mutabakat</div>
                <div className="font-heading font-bold text-lg">Ödenmiş Faturalar ↔ Cari Kayıtları</div>
                <div className="text-xs text-neutral-500 mt-0.5">Faturası &quot;Ödendi&quot; işaretli fakat cariye ödeme kaydı düşmemiş tedarikçileri tespit eder ve otomatik düzeltir.</div>
              </div>
              <button onClick={() => setShowRecon(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {reconLoading ? (
                <div className="text-center py-12 text-sm text-neutral-500">Analiz ediliyor...</div>
              ) : !recon ? null : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="bg-[#FEE2E2] border border-[#DC2626] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#DC2626]">Uyumsuz</div>
                      <div className="num font-bold text-xl mt-1 text-[#DC2626]">{recon.totals.mismatch_count}</div>
                      <div className="text-[10px] text-[#DC2626]">Ödendi ama cari boş</div>
                    </div>
                    <div className="bg-[#FFFBEB] border border-[#F59E0B] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#92400E]">Fazla Ödeme</div>
                      <div className="num font-bold text-xl mt-1 text-[#F59E0B]">{recon.totals.overpaid_count}</div>
                      <div className="text-[10px] text-[#92400E]">Cari &gt; fatura</div>
                    </div>
                    <div className="bg-[#DCFCE7] border border-[#16A34A] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#166534]">Denk</div>
                      <div className="num font-bold text-xl mt-1 text-[#16A34A]">{recon.totals.clean_count}</div>
                      <div className="text-[10px] text-[#166534]">Sorunsuz</div>
                    </div>
                    <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eksik Toplam</div>
                      <div className="num font-bold text-xl mt-1 text-[#DC2626]">{formatTL(recon.totals.grand_missing)}</div>
                      <div className="text-[10px] text-neutral-500">Cari&apos;ye eklenecek</div>
                    </div>
                  </div>

                  {recon.mismatches.length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-[#16A34A] bg-[#F0FDF4]">
                      <CheckCircle2 className="w-10 h-10 mx-auto text-[#16A34A] mb-2" />
                      <div className="text-sm font-semibold text-[#166534]">Tüm tedarikçiler dengeli.</div>
                      <div className="text-xs text-neutral-500 mt-1">Ödenmiş faturalar cari kayıtlarıyla eşleşiyor.</div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
                          Uyumsuz Tedarikçiler ({recon.mismatches.length})
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid="recon-select-all"
                            onClick={() => setSelectedForFix(new Set(recon.mismatches.map(m => m.id)))}
                            className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-[#E5E5E5] hover:bg-[#F5F5F5]"
                          >Tümünü Seç</button>
                          <button
                            data-testid="recon-clear-all"
                            onClick={() => setSelectedForFix(new Set())}
                            className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 border border-[#E5E5E5] hover:bg-[#F5F5F5]"
                          >Temizle</button>
                        </div>
                      </div>
                      <div className="border border-[#E5E5E5] overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr>
                              <th className="px-2 py-2 w-8"></th>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tedarikçi</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ödenmiş Fatura</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Kayıtlı Ödeme</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Banka</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Eklenecek</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recon.mismatches.map(m => (
                              <tr key={m.id} className="border-b border-[#F5F5F5]" data-testid={`recon-row-${m.id}`}>
                                <td className="px-2 py-2">
                                  <input
                                    type="checkbox"
                                    data-testid={`recon-check-${m.id}`}
                                    checked={selectedForFix.has(m.id)}
                                    onChange={() => toggleReconRow(m.id)}
                                  />
                                </td>
                                <td className="px-3 py-2">
                                  <div className="font-semibold">{m.name}</div>
                                  <div className="text-[10px] text-neutral-500">{m.category} {m.tax_no && `· VKN ${m.tax_no}`}</div>
                                </td>
                                <td className="px-3 py-2 text-right num text-[#DC2626] font-semibold">{formatTL(m.paid_invoices_total)}</td>
                                <td className="px-3 py-2 text-right num text-[#16A34A]">{formatTL(m.payment_total)}</td>
                                <td className="px-3 py-2 text-right num text-[#0020C2]">{formatTL(m.bank_matched_total)}</td>
                                <td className="px-3 py-2 text-right num text-[#DC2626] font-bold">
                                  <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{formatTL(m.delta)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-[#FAFAFA] border-t-2 border-[#E5E5E5]">
                            <tr>
                              <td colSpan={5} className="px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold text-right">Seçili Toplam ({selectedForFix.size} adet)</td>
                              <td className="px-3 py-2 text-right num font-bold text-[#DC2626]">
                                {formatTL(recon.mismatches.filter(m => selectedForFix.has(m.id)).reduce((s, m) => s + m.delta, 0))}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </>
                  )}

                  {recon.overpaid.length > 0 && (
                    <div className="mt-6">
                      <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-2">
                        Fazla Ödeme / Ön Ödeme ({recon.overpaid.length})
                      </div>
                      <div className="border border-[#F59E0B] bg-[#FFFBEB] p-3 text-xs text-[#92400E] mb-2">
                        Bu tedarikçilerde kayıtlı ödemeler, ödenmiş fatura toplamından fazla. Ön ödeme veya fazla ödeme olabilir. Bu durum otomatik olarak düzeltilmez, elle kontrol edin.
                      </div>
                      <div className="border border-[#E5E5E5] overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tedarikçi</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ödenmiş Fatura</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Toplam Ödeme</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fazlalık</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recon.overpaid.map(m => (
                              <tr key={m.id} className="border-b border-[#F5F5F5]">
                                <td className="px-3 py-2">
                                  <div className="font-semibold">{m.name}</div>
                                  <div className="text-[10px] text-neutral-500">{m.category}</div>
                                </td>
                                <td className="px-3 py-2 text-right num">{formatTL(m.paid_invoices_total)}</td>
                                <td className="px-3 py-2 text-right num text-[#16A34A]">{formatTL(m.recorded_paid_total)}</td>
                                <td className="px-3 py-2 text-right num font-bold text-[#F59E0B]">{formatTL(Math.abs(m.delta))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            {recon && recon.mismatches.length > 0 && (
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <button
                  onClick={() => setShowRecon(false)}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5]"
                >Kapat</button>
                <button
                  data-testid="recon-apply-btn"
                  disabled={reconApplying || selectedForFix.size === 0}
                  onClick={applyReconciliation}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#DC2626] text-white hover:bg-[#B91C1C] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reconApplying ? "Uygulanıyor..." : `${selectedForFix.size} Tedarikçiyi Düzelt`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dedupe Suppliers modal */}
      {showDedupe && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowDedupe(false)}>
          <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-md shadow-lg flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Mükerrer Tedarikçi Birleştir</div>
                <div className="font-heading font-bold text-lg">Aynı Firma, Farklı İsim Yazımı</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Örn: &quot;DİYAR SOSYAL TES.&quot; ve &quot;DİYAR SOSYAL TESİS&quot; aynı firma. Faturalar ve ödemeler tutulan kayda taşınır, diğerleri silinir.
                </div>
              </div>
              <button onClick={() => setShowDedupe(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {dedupeLoading ? (
                <div className="text-center py-12 text-sm text-neutral-500">Analiz ediliyor...</div>
              ) : !dedupe ? null : dedupe.group_count === 0 ? (
                <div className="text-center py-10 border border-dashed border-[#16A34A] bg-[#F0FDF4]">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-[#16A34A] mb-2" />
                  <div className="text-sm font-semibold text-[#166534]">Mükerrer tedarikçi bulunamadı.</div>
                  <div className="text-xs text-neutral-500 mt-1">Toplam {dedupe.total_suppliers} tedarikçi kaydı temiz.</div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="bg-[#F5F3FF] border border-[#7C3AED] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#7C3AED]">Mükerrer Grup</div>
                      <div className="num font-bold text-xl mt-1 text-[#7C3AED]">{dedupe.group_count}</div>
                    </div>
                    <div className="bg-[#FEE2E2] border border-[#DC2626] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#DC2626]">Silinecek Kayıt</div>
                      <div className="num font-bold text-xl mt-1 text-[#DC2626]">{dedupe.duplicates_to_remove}</div>
                    </div>
                    <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Tedarikçi</div>
                      <div className="num font-bold text-xl mt-1">{dedupe.total_suppliers}</div>
                      <div className="text-[10px] text-neutral-500">İşlem sonrası: {dedupe.total_suppliers - dedupe.duplicates_to_remove}</div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {dedupe.groups.map(g => (
                      <div key={g.key} className="border border-[#E5E5E5] rounded-md overflow-hidden" data-testid={`dedupe-group-${g.key}`}>
                        <div className="bg-[#FAFAFA] px-3 py-2 border-b border-[#E5E5E5] flex items-center justify-between">
                          <div className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">
                            Grup: <span className="text-[#7C3AED]">{g.key}</span> · {g.count} kayıt
                          </div>
                          <div className="text-[10px] text-neutral-500">Tutulacak kaydı seçin ↓</div>
                        </div>
                        <table className="w-full text-xs">
                          <thead className="bg-white border-b border-[#F5F5F5]">
                            <tr>
                              <th className="px-2 py-2 w-8"></th>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">İsim (DB)</th>
                              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">VKN</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Fatura</th>
                              <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ödeme</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.records.map(r => {
                              const isKeeper = keeperMap[g.key] === r.id;
                              return (
                                <tr key={r.id} className={`border-b border-[#F5F5F5] ${isKeeper ? "bg-[#DCFCE7]" : ""}`}>
                                  <td className="px-2 py-2">
                                    <input
                                      type="radio"
                                      name={`keeper-${g.key}`}
                                      data-testid={`dedupe-keep-${r.id}`}
                                      checked={isKeeper}
                                      onChange={() => setKeeperMap({ ...keeperMap, [g.key]: r.id })}
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className={`font-semibold ${isKeeper ? "text-[#16A34A]" : ""}`}>
                                      {r.name}
                                      {isKeeper && <span className="ml-2 text-[9px] uppercase tracking-widest bg-[#16A34A] text-white px-1.5 py-0.5">TUTULACAK</span>}
                                    </div>
                                    <div className="text-[10px] text-neutral-500">{r.category || "-"}</div>
                                  </td>
                                  <td className="px-3 py-2 num text-[10px] text-neutral-600">{r.tax_no || "-"}</td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="num font-semibold">{r.invoice_count}</div>
                                    <div className="text-[9px] text-neutral-500">{formatTL(r.invoice_total)}</div>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="num font-semibold text-[#16A34A]">{r.payment_count}</div>
                                    <div className="text-[9px] text-neutral-500">{formatTL(r.payment_total)}</div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {dedupe && dedupe.group_count > 0 && (
              <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#E5E5E5] bg-[#FAFAFA]">
                <button
                  onClick={() => setShowDedupe(false)}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] bg-white hover:bg-[#F5F5F5]"
                >Vazgeç</button>
                <button
                  data-testid="dedupe-apply-btn"
                  disabled={dedupeApplying}
                  onClick={applyDedupe}
                  className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {dedupeApplying ? "Uygulanıyor..." : `${dedupe.duplicates_to_remove} Mükerrer Kaydı Birleştir`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add / Edit supplier modal */}
      {(editingSup || addingSup) && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => { setEditingSup(null); setAddingSup(false); }}>
          <div className="bg-white w-full max-w-lg rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{addingSup ? "Yeni Cari Kart Ekle" : "Tedarikçi Düzenle"}</div>
                <div className="font-heading font-bold text-lg">{addingSup ? "Yeni Tedarikçi" : editingSup?.name}</div>
              </div>
              <button data-testid="edit-supplier-close" onClick={() => { setEditingSup(null); setAddingSup(false); }} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={addingSup ? submitAddSupplier : submitEditSupplier} className="p-6 space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İsim *</label>
                <input data-testid="edit-name" required value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Kategori</label>
                  <input value={editForm.category} onChange={e => setEditForm({...editForm, category: e.target.value})} placeholder="Yakıt, Bakım, vs." className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Plaka</label>
                  <input value={editForm.plate} onChange={e => setEditForm({...editForm, plate: e.target.value})} placeholder="34 ABC 123" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vergi Dairesi</label>
                  <input value={editForm.tax_office} onChange={e => setEditForm({...editForm, tax_office: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vergi No / VKN</label>
                  <input value={editForm.tax_no} onChange={e => setEditForm({...editForm, tax_no: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">IBAN</label>
                <input value={editForm.iban} onChange={e => setEditForm({...editForm, iban: e.target.value})} placeholder="TR00 0000 0000 0000 0000 0000 00" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Telefon</label>
                  <input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">İlgili Kişi</label>
                  <input value={editForm.contact} onChange={e => setEditForm({...editForm, contact: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Adres</label>
                <input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => { setEditingSup(null); setAddingSup(false); }} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="edit-supplier-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">{addingSup ? "Ekle" : "Kaydet"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit cari event modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => setEditingEvent(null)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  {editingEvent.type === "payment" ? "Ödeme Düzenle" : "Fatura Düzenle"}
                </div>
                <div className="font-heading font-bold text-lg">{selected?.name}</div>
              </div>
              <button data-testid="event-edit-close" onClick={() => setEditingEvent(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitEditEvent} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                  <input data-testid="event-edit-date" type="date" required value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                  <input data-testid="event-edit-amount" type="number" step="any" required value={eventForm.amount} onChange={e => setEventForm({...eventForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama</label>
                <input value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} placeholder="Not" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setEditingEvent(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="event-edit-submit" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
